import { useMemo } from "react";
import { useV2GetOpenRouterKeyQuery } from "@generated";
import { OpenRouterKeyFormState } from "../types";

const useGetOpenRouterKey = (id: string) => {
  const { data, error, isLoading, refetch } = useV2GetOpenRouterKeyQuery(
    { id },
    { refetchOnMountOrArgChange: true }
  );

  const { openRouterKey, customError } = useMemo(() => {
    if (!data?.data) return { openRouterKey: null, customError: null };
    try {
      const resource = data.data;
      if (!resource.id) {
        throw new Error("Missing open router key id");
      }

      const attrs = resource.attributes as Record<string, any> | undefined;
      if (!attrs) {
        throw new Error("Missing attributes");
      }

      return {
        openRouterKey: {
          id: String(resource.id),
          name: attrs["name"] || "",
          llms: Array.isArray(attrs["llms"]) ? attrs["llms"] : [],
          keyType:
            (attrs["key-type"] as OpenRouterKeyFormState["keyType"]) || "",
          default: attrs["default"] === true,
          // Azure
          modelSlug: attrs["model-slug"] || "",
          endpointUrl: attrs["endpoint-url"] || "",
          apiKey: attrs["api-key"] || "",
          modelId: attrs["model-id"] || "",
          // AWS Bedrock
          key: attrs["key"] || "",
          accessKeyId: attrs["access-key-id"] || "",
          secretAccessKey: attrs["secret-access-key"] || "",
          region: attrs["region"] || "",
          // Google Vertex
          modelType: attrs["model-type"] || "",
          projectId: attrs["project-id"] || "",
          privateKeyId: attrs["private-key-id"] || "",
          privateKey: attrs["private-key"] || "",
          clientEmail: attrs["client-email"] || "",
          clientId: attrs["client-id"] || "",
          authUri: attrs["auth-uri"] || "",
          tokenUri: attrs["token-uri"] || "",
          authProviderX509CertUrl: attrs["auth-provider-x509-cert-url"] || "",
          clientX509CertUrl: attrs["client-x509-cert-url"] || "",
          universeDomain: attrs["universe-domain"] || "",
        } as OpenRouterKeyFormState,
        customError: null,
      };
    } catch (err) {
      return {
        openRouterKey: null,
        customError:
          err instanceof Error ? err.message : "An unknown error occurred",
      };
    }
  }, [data]);

  return {
    openRouterKey,
    error: error || customError,
    isLoading,
    refetch,
  };
};

export default useGetOpenRouterKey;
